/**
 * App-store screenshot scenes at `/prototype/mobile/word-wheel/screenshot/:slug`.
 *
 * `layout` — marketing composition for the page.
 * `journeyLevel` — season catalog level (1–1100).
 * `demoFoundWordCount` — how many target words to reveal on the grid.
 * `dictionaryWord` — word to highlight in the dictionary card (must be in the puzzle).
 * `background` — photo key: cropped IPHONE_BACKGROUND (`iphone_1`…`iphone_6`)
 * or a catalog key from `public/bg_images/word-wheel`.
 * `backgroundSlidingId` / `backgroundJigsawId` — published puzzle id. The card uses that
 * row's standalone image. Do not key the photo by story id.
 * `theme` — play chrome: light | dark | random.
 *
 * Levels / backgrounds chosen for marketing variety (matches Android capture set 01–08).
 */

const SCENE_DEFAULTS = {
    season: "season_2026",
    demoFoundWordCount: 2,
    theme: "random",
    layout: "gameplay",
};

/**
 * Layouts for screenshots 01–08 (marketing).
 * @typedef {'hero'|'gameplay'|'clue'|'progression'|'journey'|'dictionary'|'themes'|'daily'|'cta'|'masterJourney'} WwScreenshotLayout
 */

export const WORD_WHEEL_SCREENSHOT_SCENES = [
    {
        ...SCENE_DEFAULTS,
        slug: "01",
        label: "Daily Word Adventure",
        layout: "hero",
        journeyLevel: 30,
        background: "iphone_1",
        backgroundSlidingId: "a628a0e3-72b3-43a6-82de-f78e450bab14",
        theme: "random",
        demoFoundWordCount: 2,
        promoHeadline: "Daily Word Adventure",
        promoSubtitle: "New, challenging crosswords every single day. Build your streak. Claim rewards!",
    },
    {
        ...SCENE_DEFAULTS,
        slug: "02",
        label: "Journey map",
        layout: "journey",
        journeyLevel: 10,
        background: "iphone_2",
        backgroundJigsawId: "6bc73d11-0e9a-43eb-bd1b-9e5387d920a1",
        theme: "random",
        promoHeadline: "Journey Map",
        promoSubtitle: "A new picture for every chapter.",
    },
    {
        ...SCENE_DEFAULTS,
        slug: "03",
        label: "Classic Challenge",
        layout: "gameplay",
        journeyLevel: 100,
        background: "iphone_3",
        theme: "random",
        demoFoundWordCount: 2,
        promoHeadline: "Classic Challenge",
        promoSubtitle: "200 Puzzles Unlocked",
    },
    {
        ...SCENE_DEFAULTS,
        slug: "04",
        label: "Hard Quest",
        layout: "gameplay",
        // Published Hard Quest board “Coin and Time” (4-letter words).
        tplId: "97c2dd63-0bd4-41fc-83a7-c07a4e017e0b",
        journeyLevel: 230,
        season: "hard_quest",
        background: "iphone_4",
        backgroundJigsawId: "a8f0aefb-1b71-4d73-9244-877c4c67a3c9",
        theme: "random",
        demoFoundWordCount: 3,
        promoHeadline: "Hard Quest",
        promoSubtitle: "Denser grids. Words of 4–8 letters.",
    },
    {
        ...SCENE_DEFAULTS,
        slug: "05",
        label: "Master Quest",
        layout: "gameplay",
        // Published board “Castle Quest” (CASTLE, QUEST, SCALE).
        tplId: "9c15555f-ca2c-400c-982b-970766a47e7e",
        journeyLevel: 105,
        season: "classic",
        background: "iphone_5",
        theme: "random",
        demoFoundWordCount: 2,
        promoHeadline: "Master Quest",
        promoSubtitle: "Expert boards. Most words are 3–8 letters.",
    },
    {
        ...SCENE_DEFAULTS,
        slug: "06",
        label: "Dictionary",
        layout: "dictionary",
        // L50 — daily unlock milestone; good vocab showcase
        journeyLevel: 50,
        background:
            "https://puzzleinteract-images.s3.us-east-1.amazonaws.com/story-preview/standalone/49f06fd5-8610-4ff5-ba06-d76fa83e0b27/d125599f-871a-413e-8a5d-06ce5c604985.png",
        theme: "random",
        demoFoundWordCount: 3,
        dictionaryWord: "TON",
        promoHeadline: "Expand Your Vocabulary",
        promoSubtitle: "Look up every word you discover.",
    },
    {
        ...SCENE_DEFAULTS,
        slug: "07",
        label: "Daily Bundle",
        layout: "daily",
        journeyLevel: 50,
        background: "iphone_1",
        theme: "dark",
        demoFoundWordCount: 2,
        promoHeadline: "Daily Bundle",
        promoSubtitle: "Unlimited Daily Access",
    },
    {
        ...SCENE_DEFAULTS,
        slug: "08",
        label: "Master",
        layout: "masterJourney",
        journeyLevel: 100,
        background: "iphone_6",
        backgroundJigsawId: "6bc73d11-0e9a-43eb-bd1b-9e5387d920a1",
        theme: "random",
        demoFoundWordCount: 3,
        promoHeadline: "The Ultimate Journey",
        promoSubtitle: "Unlock this journey",
    },
];

/** @param {string} slug */
export function getScreenshotScene(slug) {
    return WORD_WHEEL_SCREENSHOT_SCENES.find((s) => s.slug === slug) ?? null;
}

export function isScreenshotSceneConfigured(scene) {
    if (scene?.layout === "journey" || scene?.layout === "daily" || scene?.layout === "masterJourney") {
        return true;
    }
    if (typeof scene?.tplId === "string" && scene.tplId.trim()) return true;
    return Boolean(scene?.journeyLevel != null && Number.isFinite(Number(scene.journeyLevel)));
}
